/**
 * Owner activity timeline — every dated event on one owner, newest first.
 *
 * Every entry carries its own timestamp: the point of the tab is answering
 * "who wrote that note, against which invoice, and when". The feed itself is
 * derived by buildOwnerActivity (src/lib/owner-activity).
 */
import { StickyNote } from "lucide-react";
import { formatDateTime } from "@/lib/v2-format";
import type { OwnerActivityEvent, Tone } from "@/lib/owner-activity";

const TONE: Record<Tone, string> = {
  teal: "bg-teal-50 text-teal-700 border-teal-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  rose: "bg-rose-50 text-rose-700 border-rose-100",
  sky: "bg-sky-50 text-sky-700 border-sky-100",
  slate: "bg-muted text-muted-foreground border-transparent",
};

export function OwnerActivityTimeline({
  events,
  emptyTitle,
  emptyDescription,
}: {
  events: OwnerActivityEvent[];
  /* The empty state is the one part of this component that is not the same
     sentence everywhere. On the admin owner profile it is about invoices and
     payment notes; on an owner's property it is about documents, approvals and
     move-ins. Overridable, defaulting to the admin copy so that surface — and
     its tests — are untouched. */
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  if (events.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed py-12 text-center">
        <StickyNote className="mx-auto mb-3 h-7 w-7 text-muted-foreground/50" />
        <p className="text-sm font-medium">{emptyTitle ?? "No activity yet"}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {emptyDescription ??
            "Invoices, payment notes and documents appear here as they happen."}
        </p>
      </div>
    );
  }

  return (
    <ol className="relative space-y-3 border-l pl-5">
      {events.map((e, i) => {
        const Icon = e.icon;
        return (
          <li key={`${e.at}-${i}`} className="relative">
            <span
              className={`absolute -left-[30px] flex h-6 w-6 items-center justify-center rounded-full border ${TONE[e.tone]}`}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="rounded-xl border bg-card px-3 py-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p className="text-sm font-medium">{e.title}</p>
                <time
                  className="shrink-0 text-xs tabular-nums text-muted-foreground"
                  dateTime={e.at}
                >
                  {formatDateTime(e.at) ?? e.at}
                </time>
              </div>
              {(e.context || e.actor) && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {e.context && <span className="font-mono">{e.context}</span>}
                  {e.context && e.actor && <span className="mx-1.5">·</span>}
                  {/* Who did it, when the event came from the audit trail —
                      derived state has no author to name. */}
                  {e.actor && <span>{e.actor}</span>}
                </p>
              )}
              {e.detail && (
                <p className="mt-1.5 whitespace-pre-line rounded-lg bg-muted/50 px-2.5 py-1.5 text-sm text-foreground">
                  {e.detail}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
